/**
 * Centralized error handling utilities
 */
import { toast } from 'sonner';
import { debug } from '@/utils/debug';

export enum ErrorType {
    NETWORK = 'NETWORK',
    AUTH = 'AUTH',
    VALIDATION = 'VALIDATION',
    SERVER = 'SERVER',
    UNKNOWN = 'UNKNOWN'
}

interface AppError {
    message?: string;
    code?: string;
    details?: string;
    status?: number;
}

/**
 * Extract a user-friendly error message from any error type
 */
export const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as AppError).message);
    }
    return 'An unexpected error occurred';
};

/**
 * Handle errors consistently across the application
 * @param error - The error to handle
 * @param context - Optional context for debugging
 * @param showToast - Whether to show a toast notification (default: true)
 */
export const handleError = (
    error: unknown,
    context?: string,
    showToast: boolean = true
): void => {
    const message = getErrorMessage(error);

    debug.captureError(error, context);

    // User-friendly toast
    if (showToast) {
        toast.error(message);
    }
};

/**
 * Handle Service Layer errors
 * Logs the error and re-throws it for the caller to handle
 */
export const handleServiceError = (error: unknown, context: string): never => {
    debug.captureError(error, context);
    throw error;
};

/**
 * Handle React Query Mutation errors
 */
export const handleMutationError = (error: unknown, context: string): void => {
    handleError(error, context, true);
};

/**
 * Handle React Query Query errors
 */
export const handleQueryError = (error: unknown, context: string): void => {
    // We typically don't show toasts for query errors on initial load 
    // to avoid spamming user if multiple queries fail, 
    // but we might want it for specific refetches.
    // Defaulting to logging only, but using handleError with false for toast
    handleError(error, context, false);
};

/**
 * Handle API errors with specific messaging
 */
export const handleApiError = (
    error: unknown,
    fallbackMessage: string = 'Failed to complete the request'
): string => {
    const appError = error as AppError;

    // Check for common Supabase error patterns
    if (appError.message?.includes('JWT')) {
        return 'Your session has expired. Please log in again.';
    }
    if (appError.message?.includes('duplicate key')) {
        return 'This record already exists.';
    }
    if (appError.message?.includes('violates foreign key')) {
        return 'Cannot complete this action due to related records.';
    }

    debug.captureError(error, 'API');
    return appError.message || fallbackMessage;
};
